<template>
  <router-link :to="route" class="subscriber-row">
    <div class="cell center">
      <div :class="statusClassList" class="badge">{{ statusText }}</div>
    </div>
    <div class="cell">
      <h5>{{ user.name }}</h5>
      <p>{{ user.phoneNumber }}</p>
      <p class="font-red">{{ user.offlinePurchase }}</p>
    </div>
    <div class="cell">
      <h5>บ้านเลขที่ {{ home.addressNumber }}</h5>
      <p>โครงการ {{ home.project }}</p>
    </div>
    <div class="cell">
      <h5>{{ packageInfo.name }}</h5>
      <p>
        <span v-if="packageInfo.originalPrice" class="original-price">{{ originalPriceText }}</span>
        {{ billingCycle }}
      </p>
    </div>
    <div class="cell">
      <p v-if="totalCoinAmount">{{ totalCoinAmount }}</p>
      <p v-if="couponCode">{{ couponCode }}<br />{{ discountAmount }}</p>
      <h5>{{ paymentAmount }}</h5>
    </div>
    <div class="cell">
      <div :class="categoryClassList" class="badge">{{ categoryText }}</div>
      <h5>{{ purchasedAtText }}</h5>
    </div>
    <div class="cell center">
      <h5>{{ quantity }}</h5>
    </div>
    <div class="cell">
      <h5>{{ currentAppointment }}</h5>
      <p>{{ appointmentDurationTime }}</p>
    </div>
  </router-link>
</template>

<script>
import { toLocaleTH } from "@/lib/dateTime";
import { formatCurrencyAndTwoDecimalsString } from "@/lib/formatter";
import { subscriptionStatus } from "@/lib/mapper";

export default {
  props: {
    user: { type: Object, default: null },
    home: { type: Object, default: null },
    packageInfo: { type: Object, default: null },
    purchasedAt: { type: Object, default: null },
    renewAt: { type: Object, default: null },
    route: { type: Object, default: null },
    status: { type: String, default: null },
    appointmentDate: { type: Object, default: null },
    category: { type: String, default: null },
    quantity: { type: Number, default: 1 },
    price: { type: Number, default: null },
    appointmentDurationTime: { type: String, default: null }
  },
  computed: {
    originalPriceText() {
      if (!this.packageInfo.originalPrice) {
        return null;
      }

      const price = formatCurrencyAndTwoDecimalsString((this.packageInfo.originalPrice / 100) * this.quantity);

      return `${price} บาท`;
    },
    totalPrice() {
      return this.price ? this.price : this.packageInfo.price;
    },
    billingCycle() {
      const totalPriceBeforeDiscount = formatCurrencyAndTwoDecimalsString(
        (this.totalPrice / 100) * this.quantity
      );
      const billingCycle =
        this.packageInfo.billingCycle > 1
          ? ` ${  this.packageInfo.billingCycle.toLocaleString()  } `
          : " ";

      return `${totalPriceBeforeDiscount} บาท /${billingCycle}${this.cycleUnit}`;
    },
    cycleUnit() {
      return this.packageInfo.billingCycle === 0 ? "ครั้ง" : "เดือน";
    },
    paymentAmount() {
      if (this.categoryText === 'free_trial') { return 'Free'; };

      const amount = this.packageInfo.paymentAmount;

      const paymentAmount = formatCurrencyAndTwoDecimalsString(amount / 100);

      return `เงินสด ${paymentAmount} บาท`;
    },
    totalCoinAmount() {
      if (this.packageInfo.totalCoinAmount) {
        const totalCoinAmount = formatCurrencyAndTwoDecimalsString(
          this.packageInfo.totalCoinAmount
        );

        return `ชำระด้วย MNCs / ${totalCoinAmount} บาท`;
      }
      return null;
    },
    couponCode() {
      if (this.packageInfo.couponCode) {
        return `ส่วนลด: ${this.packageInfo.couponCode} /`;
      }
      return null;
    },
    discountAmount() {
      if (this.couponCode) {
        let totalPrice = (this.totalPrice / 100) * this.quantity;

        if (this.packageInfo.discountAmount) {
          totalPrice = this.packageInfo.discountAmount / 100;
        } else if (this.packageInfo.totalCoinAmount) {
          totalPrice -= this.packageInfo.totalCoinAmount;
        }

        return `${formatCurrencyAndTwoDecimalsString(totalPrice)} บาท`;
      }
      return null;
    },
    purchasedAtText() {
      // A subscribe (recurring) package may have renewed many times since the original
      // purchase, so its current billing cycle date is more relevant here than the
      // first purchase date. One-time and free-trial packages only ever have one cycle,
      // so purchasedAt already reflects it.
      const displayDate = this.category === subscriptionStatus.SUBSCRIBE ? this.renewAt : this.purchasedAt;

      return toLocaleTH(displayDate).toFormat("dd MMMM yyyy HH:mm น.");
    },
    renewAtText() {
      if (this.status === subscriptionStatus.ONE_TIME) {
        return "-";
      }

      if (this.purchasedAt.hasSame(this.renewAt, "day")) {
        return "-";
      }

      return toLocaleTH(this.renewAt).toFormat("dd MMMM yyyy HH:mm น.");
    },
    statusText() {
      return this.status.toLowerCase();
    },
    statusClassList() {
      return [this.statusText];
    },
    currentAppointment() {
      let appointmentDate = 'รอนัดหมาย'
      if (this.appointmentDate) {
        appointmentDate = toLocaleTH(this.appointmentDate).toFormat("dd MMMM yyyy")

      } else if (this.status === 'CANCELLED') {
        appointmentDate = 'ยกเลิกแพ็กเกจ'
      }

      return appointmentDate
    },
    categoryText() {
      return this.category.toLowerCase();
    },
    categoryClassList() {
      return [this.categoryText];
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.subscriber-row {
  display: grid;
  grid-template-columns: 120px repeat(4, 1fr) 200px 100px repeat(1, 1fr);
  grid-gap: 15px;
  align-items: center;

  padding: ($space / 2) $space;

  &:hover {
    background: #fafafa;
    cursor: pointer;
    text-decoration: none;
  }
}

.center {
  text-align: center;
}

.badge {
  display: inline-block;
  font-size: 0.9rem;
  padding: 4px 10px;
  border-radius: 4px;

  &.active {
    background-color: $success-color;
  }

  &.unsubscribed {
    background-color: $warning-color;
  }

  &.cancelled {
    background-color: $danger-color;
  }

  &.free_trial {
    background-color: $secondary-color;
  }

  &.one_time {
    background-color: $primary-color;
  }

  &.subscribe {
    background-color: $success-color;
  }
}

.subscriber-row > .cell {
  > h5 {
    color: black;
    font-size: 16px;
  }

  > p {
    font-size: 14px;
  }

  > h5,
  > p {
    line-height: 1.6;
    margin: 0;
  }
}

.original-price {
  text-decoration: line-through;
}

.font-red {
  color: rgb(250, 102, 102);
}
</style>
